import { useEffect, useRef, useState, FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { transfersApi, Transfer } from "../api/transfers";
import { vesselsApi, Vessel } from "../api/vessels";
import { authApi } from "../api/auth";

interface NewTransferForm {
  vesselId: string;
  recipientEmail: string;
  description: string;
  expiresInHours: string;
}

const EMPTY_FORM: NewTransferForm = {
  vesselId: "",
  recipientEmail: "",
  description: "",
  expiresInHours: "72",
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function TransfersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [allowExternalSend, setAllowExternalSend] = useState(false);

  // ── New Transfer modal ────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<NewTransferForm>(EMPTY_FORM);
  const [recipientTab, setRecipientTab] = useState<"vessel" | "direct">("vessel");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [submitStep, setSubmitStep] = useState<null | "uploading" | "creating">(null);
  const [formError, setFormError] = useState<string | null>(null);

  // ── Delete confirmation ───────────────────────────────────────────────────
  const [deletingTransfer, setDeletingTransfer] = useState<Transfer | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState<{ msg: string; kind: "success" | "error" } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function showToast(msg: string, kind: "success" | "error" = "success") {
    setToast({ msg, kind });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }

  function loadTransfers() {
    setLoading(true);
    transfersApi
      .list()
      .then(setTransfers)
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadTransfers();
    vesselsApi.list().then(setVessels).catch(console.error);
    authApi.me().then((u) => setAllowExternalSend(u.allow_external_send)).catch(console.error);
  }, []);

  // Auto-open when navigated with ?new=1
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      openModal();
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // ── Modal helpers ─────────────────────────────────────────────────────────
  function openModal() {
    setForm(EMPTY_FORM);
    setSelectedFile(null);
    setFormError(null);
    setSubmitStep(null);
    setRecipientTab("vessel");
    setShowModal(true);
  }

  function closeModal() {
    if (submitStep) return;
    setShowModal(false);
  }

  function handleFileSelect(file: File | null) {
    if (!file) return;
    const MAX = 100 * 1024 * 1024;
    if (file.size > MAX) {
      setFormError("File exceeds the 100 MB limit.");
      return;
    }
    setFormError(null);
    setSelectedFile(file);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!selectedFile) { setFormError("Please select a file."); return; }

    const isDirect = allowExternalSend && recipientTab === "direct";

    if (!isDirect && !form.vesselId) {
      setFormError("Please select a vessel.");
      return;
    }
    if (!form.recipientEmail) {
      setFormError("Recipient email is required.");
      return;
    }

    setFormError(null);

    // Step 1 — upload file to R2
    setSubmitStep("uploading");
    let r2Key: string;
    try {
      const up = await transfersApi.upload(selectedFile);
      r2Key = up.r2Key;
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "File upload failed");
      setSubmitStep(null);
      return;
    }

    // Step 2 — create transfer record
    setSubmitStep("creating");
    try {
      await transfersApi.create({
        ...(isDirect ? {} : { vesselId: form.vesselId }),
        recipientEmail: form.recipientEmail,
        description: form.description,
        expiresInHours: Number(form.expiresInHours) || 72,
        r2Key,
      });
      setShowModal(false);
      loadTransfers();
      showToast("Transfer created");
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to create transfer");
    } finally {
      setSubmitStep(null);
    }
  }

  // ── Delete ────────────────────────────────────────────────────────────────
  async function handleDelete() {
    if (!deletingTransfer) return;
    setDeleteSubmitting(true);
    try {
      await transfersApi.delete(deletingTransfer.id);
      setTransfers((ts) => ts.filter((t) => t.id !== deletingTransfer.id));
      setDeletingTransfer(null);
      showToast("Transfer deleted");
    } catch {
      showToast("Failed to delete transfer", "error");
      setDeletingTransfer(null);
    } finally {
      setDeleteSubmitting(false);
    }
  }

  const submitting = submitStep !== null;
  const isDirect = allowExternalSend && recipientTab === "direct";

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[60] rounded-lg px-5 py-3 text-sm font-medium text-white shadow-lg ${
          toast.kind === "error" ? "bg-red-600" : "bg-green-600"
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Page header */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Transfers</h2>
          <p className="text-sm text-gray-500 mt-0.5">All document transfers</p>
        </div>
        <button
          onClick={openModal}
          className="rounded-lg bg-navy-700 px-4 py-2 text-sm font-semibold text-white hover:bg-navy-800 transition-colors"
        >
          New Transfer
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Vessel</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Recipient</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Sent</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Expires</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transfers.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3 text-gray-800 font-medium">{t.description}</td>
                    <td className="px-5 py-3 text-gray-600">{t.vessel_name ?? "—"}</td>
                    <td className="px-5 py-3 text-gray-600">{t.recipient_email}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        t.status === "delivered"  ? "bg-green-100 text-green-700"  :
                        t.status === "pending"    ? "bg-amber-100 text-amber-700"  :
                        t.status === "in_transit" ? "bg-blue-100 text-blue-700"    :
                                                    "bg-red-100 text-red-700"
                      }`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-gray-500">
                      {new Date(t.created_at).toLocaleDateString([], { day: "numeric", month: "short" })}
                    </td>
                    <td className="px-5 py-3 text-gray-500">
                      {new Date(t.expires_at).toLocaleDateString([], { day: "numeric", month: "short" })}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => setDeletingTransfer(t)}
                        className="text-xs font-medium text-red-600 hover:text-red-800 transition-colors"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {transfers.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-10 text-center text-sm text-gray-400">
                      No transfers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Delete confirmation ───────────────────────────────────────────── */}
      {deletingTransfer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={(e) => { if (e.target === e.currentTarget) setDeletingTransfer(null); }}
        >
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Delete transfer?</h2>
            <p className="text-sm text-gray-600 mb-6">
              Delete <span className="font-medium text-gray-900">"{deletingTransfer.description}"</span>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setDeletingTransfer(null)}
                className="flex-1 rounded-lg border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteSubmitting}
                className="flex-1 rounded-lg bg-red-600 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteSubmitting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── New Transfer modal ──────────────────────────────────────────────── */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-gray-900">New Transfer</h2>
              <button
                onClick={closeModal}
                disabled={submitting}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none disabled:opacity-40"
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            {formError && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {formError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* ── File upload ── */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File <span className="text-red-500">*</span>
                </label>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    handleFileSelect(e.dataTransfer.files[0] ?? null);
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  className={`rounded-lg border-2 border-dashed px-4 py-5 text-center cursor-pointer transition-colors ${
                    dragOver
                      ? "border-navy-500 bg-navy-50"
                      : selectedFile
                      ? "border-green-400 bg-green-50"
                      : "border-gray-300 hover:border-navy-400"
                  }`}
                >
                  {selectedFile ? (
                    <div>
                      <p className="text-sm font-medium text-gray-800 truncate">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{formatBytes(selectedFile.size)}</p>
                      <p className="text-xs text-navy-600 mt-1">Click to change</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-500">Drag a file here or <span className="text-navy-700 font-medium">browse</span></p>
                      <p className="text-xs text-gray-400 mt-1">Any file type · max 100 MB</p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={(e) => handleFileSelect(e.target.files?.[0] ?? null)}
                  />
                </div>
              </div>

              {/* ── Recipient tabs (only when allow_external_send) ── */}
              {allowExternalSend && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recipient <span className="text-red-500">*</span>
                  </label>
                  <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => {
                        setRecipientTab("vessel");
                        setForm((f) => ({ ...f, recipientEmail: "" }));
                      }}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium transition-colors ${
                        recipientTab === "vessel"
                          ? "bg-navy-700 text-white"
                          : "bg-white text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {/* Ship icon */}
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2s2.5 2 5 2 2.5-2 5-2c1.3 0 1.9.5 2.5 1"/>
                        <path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76"/>
                        <path d="M19 13V7l-7-3-7 3v6"/>
                        <line x1="12" y1="10" x2="12" y2="4"/>
                      </svg>
                      Vessel
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setRecipientTab("direct");
                        setForm((f) => ({ ...f, vesselId: "", recipientEmail: "" }));
                      }}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium transition-colors border-l border-gray-300 ${
                        recipientTab === "direct"
                          ? "bg-navy-700 text-white"
                          : "bg-white text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {/* Send / paper plane icon */}
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13"/>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                      </svg>
                      Direct Send
                    </button>
                  </div>
                </div>
              )}

              {/* ── Vessel select (vessel tab or no tabs) ── */}
              {!isDirect && (
                <div>
                  {!allowExternalSend && (
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vessel <span className="text-red-500">*</span>
                    </label>
                  )}
                  <select
                    value={form.vesselId}
                    onChange={(e) => {
                      const id = e.target.value;
                      const vessel = vessels.find((v) => v.id === id);
                      setForm((f) => ({
                        ...f,
                        vesselId: id,
                        recipientEmail: id === "" ? "" : (vessel?.contact_email ?? f.recipientEmail),
                      }));
                    }}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 bg-white"
                  >
                    <option value="">Select a vessel…</option>
                    {vessels.filter((v) => v.active).map((v) => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* ── Recipient Email ── */}
              <div>
                {!allowExternalSend && (
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recipient Email <span className="text-red-500">*</span>
                  </label>
                )}
                {isDirect ? (
                  <>
                    <input
                      type="email"
                      required
                      value={form.recipientEmail}
                      onChange={(e) => setForm((f) => ({ ...f, recipientEmail: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                      placeholder="recipient@company.com"
                    />
                    <p className="mt-1 text-xs text-gray-400">Send to any email — no vessel account needed</p>
                  </>
                ) : (
                  <input
                    type="email"
                    required
                    value={form.recipientEmail}
                    onChange={(e) => setForm((f) => ({ ...f, recipientEmail: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                    placeholder="recipient@example.com"
                  />
                )}
              </div>

              {/* ── Description ── */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <input
                  required
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  placeholder="e.g. Crew documents for MV Example"
                />
              </div>

              {/* ── Expiry ── */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expires in
                </label>
                <select
                  value={form.expiresInHours}
                  onChange={(e) => setForm((f) => ({ ...f, expiresInHours: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 bg-white"
                >
                  <option value="24">24 hours</option>
                  <option value="48">48 hours</option>
                  <option value="72">72 hours (default)</option>
                  <option value="168">7 days</option>
                </select>
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={submitting}
                  className="flex-1 rounded-lg border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !selectedFile}
                  className="flex-1 rounded-lg bg-navy-700 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-50"
                >
                  {submitStep === "uploading"
                    ? "Uploading file…"
                    : submitStep === "creating"
                    ? "Creating transfer…"
                    : "Create Transfer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
